import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { getSectionPerms, isSubItemVisible } from '../../permissions';
import {
  AlertCircle,
  CalendarCheck,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Loader2,
  LogIn,
  LogOut,
  RefreshCw,
  Save,
  UserCheck,
  UserX,
  Users,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const indiaToday = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
};

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const formatDay = value => new Date(`${value}T00:00:00`).toLocaleDateString('en-IN', {
  weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
});

const formatSessionTime = value => value ? new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit'
}).format(new Date(value)) : '—';

const shiftDate = (value, amount) => {
  const date = new Date(`${value}T00:00:00`);
  date.setDate(date.getDate() + amount);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const SummaryCard = ({ icon: Icon, label, value, tone }) => (
  <div className="rounded-sm border border-slate-200 bg-white p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
      </div>
      <span className={`rounded-full p-2.5 ${tone}`}><Icon className="h-5 w-5" /></span>
    </div>
  </div>
);

export const Attendance = () => {
  const canView = isSubItemVisible('hr', 'attendance');
  const permissions = getSectionPerms('attendance');
  const [selectedDate, setSelectedDate] = useState(indiaToday());
  const [rows, setRows] = useState([]);
  const [dirtyUsers, setDirtyUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const isPastDate = selectedDate < indiaToday();
  const canChangeAttendance = (permissions.canAdd || permissions.canEdit) && !isPastDate;
  const canChangeRow = row => canChangeAttendance && (
    row.status === 'not_marked' ? permissions.canAdd : permissions.canEdit
  );

  const fetchAttendance = useCallback(async () => {
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/hr/attendance?date=${encodeURIComponent(selectedDate)}`,
        authHeaders()
      );
      setRows(response.data.employees || []);
      setDirtyUsers({});
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [canView, selectedDate]);

  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

  const summary = useMemo(() => ({
    total: rows.length,
    present: rows.filter(row => row.status === 'present').length,
    absent: rows.filter(row => row.status === 'absent').length,
    onLeave: rows.filter(row => row.absence_type === 'leave').length,
    notMarked: rows.filter(row => row.status === 'not_marked').length,
  }), [rows]);

  const updateRow = (userId, changes) => {
    const row = rows.find(item => item.user_id === userId);
    if (!row || !canChangeRow(row)) return;
    setRows(current => current.map(item => item.user_id === userId ? { ...item, ...changes } : item));
    setDirtyUsers(current => ({ ...current, [userId]: true }));
    setSuccess('');
  };

  const markAll = status => {
    if (!canChangeAttendance) return;
    const changed = {};
    setRows(current => current.map(row => {
      if (row.locked || !canChangeRow(row)) return row;
      changed[row.user_id] = true;
      return {
        ...row,
        status,
        absence_type: status === 'absent' ? 'manual' : null,
        source: 'manual',
      };
    }));
    setDirtyUsers(current => ({ ...current, ...changed }));
    setSuccess('');
  };

  const saveAttendance = async () => {
    if (!canChangeAttendance) return;
    const changedIds = Object.keys(dirtyUsers);
    if (!changedIds.length) return;
    const entries = rows
      .filter(row => changedIds.includes(row.user_id) && !row.locked && ['present', 'absent'].includes(row.status))
      .map(row => ({ user_id: row.user_id, status: row.status, notes: row.notes || null }));
    if (!entries.length) return;

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/hr/attendance/${selectedDate}`,
        { entries },
        authHeaders()
      );
      setRows(response.data.employees || []);
      setDirtyUsers({});
      setSuccess('Attendance saved successfully');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  if (!canView) return (
    <div className="rounded-sm border border-red-200 bg-red-50 p-6 text-red-700" data-testid="hr-access-denied">
      <div className="flex items-center gap-2 font-semibold"><AlertCircle className="h-5 w-5" /> Access denied</div>
      <p className="mt-1 text-sm">Your role does not have permission to view Attendance.</p>
    </div>
  );

  return (
    <div data-testid="attendance-page">
      <div className="mb-8 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight text-slate-900">Attendance</h1>
          <p className="mt-1 text-slate-600">Mark and review daily staff attendance</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setSelectedDate(shiftDate(selectedDate, -1))}
            className="rounded-sm border border-slate-200 bg-white p-2.5 text-slate-600 hover:bg-slate-50" aria-label="Previous day">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <input type="date" value={selectedDate} onChange={event => setSelectedDate(event.target.value)}
            data-testid="attendance-date" className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
          <button type="button" onClick={() => setSelectedDate(shiftDate(selectedDate, 1))}
            className="rounded-sm border border-slate-200 bg-white p-2.5 text-slate-600 hover:bg-slate-50" aria-label="Next day">
            <ChevronRight className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => setSelectedDate(indiaToday())}
            className="rounded-sm border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Today</button>
          <button type="button" onClick={fetchAttendance} disabled={loading}
            className="rounded-sm border border-slate-200 bg-white p-2.5 text-slate-600 hover:bg-slate-50 disabled:opacity-50" aria-label="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <SummaryCard icon={Users} label="Total Staff" value={summary.total} tone="bg-slate-100 text-slate-600" />
        <SummaryCard icon={UserCheck} label="Present" value={summary.present} tone="bg-green-100 text-green-700" />
        <SummaryCard icon={UserX} label="Absent" value={summary.absent} tone="bg-red-100 text-red-700" />
        <SummaryCard icon={CalendarCheck} label="On Leave" value={summary.onLeave} tone="bg-amber-100 text-amber-700" />
        <SummaryCard icon={Clock3} label="Not Marked" value={summary.notMarked} tone="bg-blue-100 text-blue-700" />
      </div>

      {error && <div className="mb-4 flex items-center gap-2 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-red-700"><AlertCircle className="h-5 w-5" />{error}</div>}
      {success && <div className="mb-4 flex items-center gap-2 rounded-sm border border-green-200 bg-green-50 px-4 py-3 text-green-700"><Check className="h-5 w-5" />{success}</div>}

      <div className="overflow-hidden rounded-sm border border-slate-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="font-bold text-slate-900">{formatDay(selectedDate)}</h2>
            <p className="text-xs text-slate-500">
              {isPastDate ? 'Past attendance is read-only.' : canChangeAttendance ? 'Leave entries are locked and included in the absent total.' : 'Your role has view-only access.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => markAll('present')} disabled={loading || !rows.length || !canChangeAttendance}
              data-testid="mark-all-present" className="rounded-sm border border-green-200 bg-green-50 px-3 py-2 text-xs font-bold uppercase tracking-wider text-green-700 hover:bg-green-100 disabled:opacity-50">Mark All Present</button>
            <button type="button" onClick={() => markAll('absent')} disabled={loading || !rows.length || !canChangeAttendance}
              data-testid="mark-all-absent" className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold uppercase tracking-wider text-red-700 hover:bg-red-100 disabled:opacity-50">Mark All Absent</button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : rows.length === 0 ? (
          <div className="py-20 text-center text-slate-500">No active users are available for attendance.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((row, index) => (
              <div key={row.user_id} data-testid={`attendance-row-${row.user_id}`}
                className={`grid gap-4 px-5 py-4 lg:grid-cols-[40px_minmax(200px,1fr)_minmax(170px,0.7fr)_minmax(270px,auto)_minmax(180px,1fr)] lg:items-center ${row.locked ? 'bg-amber-50/40' : ''}`}>
                <span className="hidden text-sm text-slate-400 lg:block">{index + 1}</span>
                <div>
                  <p className="font-semibold text-slate-900">{row.user_name}</p>
                  <p className="text-xs text-slate-500">{row.role_name || 'No role'}{row.email ? ` · ${row.email}` : ''}</p>
                </div>
                <div className="space-y-1 text-xs" data-testid={`session-times-${row.user_id}`}>
                  <p className="flex items-center gap-1.5 text-slate-600"><LogIn className="h-3.5 w-3.5 text-green-600" /><span className="font-medium">First login:</span> {formatSessionTime(row.first_login_at)}</p>
                  <p className="flex items-center gap-1.5 text-slate-600"><LogOut className="h-3.5 w-3.5 text-red-500" /><span className="font-medium">Last logout:</span> {formatSessionTime(row.last_logout_at)}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {row.locked ? (
                    <span className="inline-flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-800">
                      <CalendarCheck className="h-4 w-4" /> On Leave{row.leave_type ? ` · ${row.leave_type}` : ''}
                    </span>
                  ) : (
                    <>
                      <button type="button" disabled={!canChangeRow(row)} onClick={() => updateRow(row.user_id, { status: 'present', absence_type: null, source: 'manual' })}
                        data-testid={`present-${row.user_id}`}
                        className={`inline-flex items-center gap-1.5 rounded-sm border px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${row.status === 'present' ? 'border-green-600 bg-green-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-green-300'}`}>
                        <UserCheck className="h-4 w-4" /> Present
                      </button>
                      <button type="button" disabled={!canChangeRow(row)} onClick={() => updateRow(row.user_id, { status: 'absent', absence_type: 'manual', source: 'manual' })}
                        data-testid={`absent-${row.user_id}`}
                        className={`inline-flex items-center gap-1.5 rounded-sm border px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${row.status === 'absent' ? 'border-red-600 bg-red-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-red-300'}`}>
                        <UserX className="h-4 w-4" /> Absent
                      </button>
                      {row.status === 'not_marked' && <span className="text-xs font-medium text-blue-600">Not marked</span>}
                    </>
                  )}
                </div>
                <input type="text" value={row.notes || ''} disabled={!canChangeRow(row) || row.locked || row.status === 'not_marked'}
                  onChange={event => updateRow(row.user_id, { notes: event.target.value })}
                  placeholder={row.locked ? (row.notes || 'Leave') : row.status === 'not_marked' ? 'Select a status first' : 'Optional note'}
                  className="w-full rounded-sm border border-slate-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none disabled:bg-amber-50 disabled:text-amber-700" />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="sticky bottom-0 z-20 mt-6 flex items-center justify-between border-t border-slate-200 bg-white/95 px-5 py-4 shadow-[0_-4px_12px_rgba(15,23,42,0.06)] backdrop-blur">
        <p className="text-sm text-slate-500">{isPastDate ? 'Past attendance cannot be changed' : !canChangeAttendance ? 'View-only access' : Object.keys(dirtyUsers).length ? `${Object.keys(dirtyUsers).length} unsaved change(s)` : 'All changes saved'}</p>
        <button type="button" onClick={saveAttendance} disabled={saving || !canChangeAttendance || !Object.keys(dirtyUsers).length}
          data-testid="save-attendance" className="inline-flex items-center gap-2 rounded-sm bg-orange-500 px-6 py-2.5 font-bold uppercase tracking-wider text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Attendance
        </button>
      </div>
    </div>
  );
};
